import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Wifi, WifiOff, Users, Volume2, VolumeX, Settings, Maximize, Minimize, Globe, ChevronLeft, FileText, Headphones, Loader2, HelpCircle, X, Monitor, Smartphone } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import translations from "@/i18n/translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Map language codes to Web Speech API voice language codes
const VOICE_LANG_MAP = {
  pt: "pt-BR", en: "en-US", es: "es-ES", fr: "fr-FR", de: "de-DE",
  it: "it-IT", zh: "zh-CN", ja: "ja-JP", ko: "ko-KR", ru: "ru-RU",
  ar: "ar-SA", hi: "hi-IN", nl: "nl-NL", pl: "pl-PL", tr: "tr-TR",
  vi: "vi-VN", th: "th-TH", sv: "sv-SE", uk: "uk-UA", fil: "fil-PH"
};

// Available languages for visitors to choose from (all 20 supported languages)
const VISITOR_LANGUAGES = [
  { code: "pt", name: "Português", flag: "🇧🇷" },
  { code: "en", name: "English", flag: "🇺🇸" },
  { code: "es", name: "Español", flag: "🇪🇸" },
  { code: "fr", name: "Français", flag: "🇫🇷" },
  { code: "de", name: "Deutsch", flag: "🇩🇪" },
  { code: "it", name: "Italiano", flag: "🇮🇹" },
  { code: "zh", name: "中文", flag: "🇨🇳" },
  { code: "ja", name: "日本語", flag: "🇯🇵" },
  { code: "ko", name: "한국어", flag: "🇰🇷" },
  { code: "ru", name: "Русский", flag: "🇷🇺" },
  { code: "ar", name: "العربية", flag: "🇸🇦" },
  { code: "hi", name: "हिन्दी", flag: "🇮🇳" },
  { code: "nl", name: "Nederlands", flag: "🇳🇱" },
  { code: "pl", name: "Polski", flag: "🇵🇱" },
  { code: "tr", name: "Türkçe", flag: "🇹🇷" },
  { code: "vi", name: "Tiếng Việt", flag: "🇻🇳" },
  { code: "th", name: "ไทย", flag: "🇹🇭" },
  { code: "sv", name: "Svenska", flag: "🇸🇪" },
  { code: "uk", name: "Українська", flag: "🇺🇦" },
  { code: "fil", name: "Filipino", flag: "🇵🇭" },
];

export default function PublicRoomPage() {
  const { roomId } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  
  // Language selection state
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    // Check if user already selected a language for this room
    const saved = localStorage.getItem(`room_${roomId}_language`);
    return saved || null;
  });
  const [showLanguageSelect, setShowLanguageSelect] = useState(!selectedLanguage);
  
  // Output mode state (text-only or text+voice)
  const [outputMode, setOutputMode] = useState(() => {
    const saved = localStorage.getItem(`room_${roomId}_outputMode`);
    return saved || null; // null means not selected yet
  });
  const [showModeSelect, setShowModeSelect] = useState(false);
  const [showVoiceSelect, setShowVoiceSelect] = useState(false);
  const [hasHeadphones, setHasHeadphones] = useState(null); // null = checking, true/false = result
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [hasNativeVoices, setHasNativeVoices] = useState(true); // Whether native voices exist for selected language
  const [selectedVoice, setSelectedVoice] = useState(() => {
    const saved = localStorage.getItem(`room_${roomId}_voice`);
    return saved || null;
  });
  const speechSynthRef = useRef(null);
  const lastSpokenTextRef = useRef("");
  const [showVoiceHelpModal, setShowVoiceHelpModal] = useState(false);
  
  // Translation function that uses the visitor's selected language (not platform language)
  const tRoom = useCallback((key) => {
    if (!selectedLanguage) return translations["en"]?.[key] || key;
    return translations[selectedLanguage]?.[key] || translations["en"]?.[key] || key;
  }, [selectedLanguage]);
  
  // Room state
  const [room, setRoom] = useState(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const [translation, setTranslation] = useState("");
  const [original, setOriginal] = useState("");
  const [viewerCount, setViewerCount] = useState(0);
  const [isTranslating, setIsTranslating] = useState(false);
  
  // Display settings
  const [fontSize, setFontSize] = useState(() => {
    return parseInt(localStorage.getItem("publicRoom_fontSize") || "48");
  });
  const [bgColor, setBgColor] = useState(() => {
    return localStorage.getItem("publicRoom_bgColor") || "#000000";
  });
  const [textColor, setTextColor] = useState(() => {
    return localStorage.getItem("publicRoom_textColor") || "#ffffff";
  });
  const [showOriginal, setShowOriginal] = useState(() => {
    return localStorage.getItem("publicRoom_showOriginal") === "true";
  });
  // Reading speed in characters per second (subtitle industry standard: 15 = Normal, Netflix)
  const [readingSpeed, setReadingSpeed] = useState(() => {
    return parseInt(localStorage.getItem("publicRoom_readingSpeed") || "15");
  });
  const [showSettings, setShowSettings] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [queueSize, setQueueSize] = useState(0);
  
  // OBS mode from URL params
  const obsMode = searchParams.get("obs") === "1";
  const urlFontSize = searchParams.get("fontSize");
  const urlTextColor = searchParams.get("textColor");
  const urlBgColor = searchParams.get("bgColor");
  const urlLang = searchParams.get("lang"); // Allow setting language via URL
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const containerRef = useRef(null);
  const lastOriginalRef = useRef("");

  // Translation display queue (sequential, NO phrase is ever dropped).
  // Each item: { text, addedAt }. Items are shown strictly in order. When a
  // backlog builds up, the hold time of the current phrase is shortened
  // (catch-up mode) and short pending fragments are merged together so the
  // display keeps pace with the speaker without skipping content.
  const translationQueueRef = useRef([]);
  const displayTimeoutRef = useRef(null);
  const currentItemRef = useRef(null); // { text, shownAt }
  const readingSpeedRef = useRef(readingSpeed);
  useEffect(() => { readingSpeedRef.current = readingSpeed; }, [readingSpeed]);

  const MERGE_MAX_CHARS = 120;

  // Hold time for a phrase. With no backlog: max(2.5s, min(12s, len/CPS+0.5s)).
  // With backlog: shrink proportionally, never below a fast-skim floor.
  const computeHoldMs = useCallback((text, backlog) => {
    if (!text) return 2500;
    const cps = readingSpeedRef.current || 15;
    const base = Math.max(2500, Math.min(12000, (text.length / cps) * 1000 + 500));
    if (backlog <= 0) return base;
    const floor = Math.max(1200, (text.length / (cps * 2.2)) * 1000);
    return Math.max(floor, base / (1 + 0.5 * backlog));
  }, []);

  const showNextInQueueRef = useRef(null);

  // (Re)schedule the advance timer for the current phrase based on backlog size
  const scheduleAdvance = useCallback(() => {
    if (displayTimeoutRef.current) clearTimeout(displayTimeoutRef.current);
    const cur = currentItemRef.current;
    if (!cur) return;
    const holdMs = computeHoldMs(cur.text, translationQueueRef.current.length);
    const remaining = Math.max(0, cur.shownAt + holdMs - Date.now());
    displayTimeoutRef.current = setTimeout(() => showNextInQueueRef.current?.(), remaining);
  }, [computeHoldMs]);

  const showNextInQueue = useCallback(() => {
    const queue = translationQueueRef.current;
    if (queue.length === 0) {
      currentItemRef.current = null;
      setQueueSize(0);
      return;
    }
    const next = queue.shift();
    setQueueSize(queue.length);
    setTranslation(next.text);
    currentItemRef.current = { text: next.text, shownAt: Date.now() };
    scheduleAdvance();
  }, [scheduleAdvance]);
  useEffect(() => { showNextInQueueRef.current = showNextInQueue; }, [showNextInQueue]);

  const enqueueTranslation = useCallback((text) => {
    if (!text) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    const queue = translationQueueRef.current;
    const last = queue[queue.length - 1];
    const cur = currentItemRef.current;
    if ((last && last.text === trimmed) || (!last && cur && cur.text === trimmed)) return;
    // Merge short consecutive fragments into one caption (keeps order, fewer flips)
    if (last && last.text.length + trimmed.length + 1 <= MERGE_MAX_CHARS) {
      last.text = `${last.text} ${trimmed}`;
    } else {
      queue.push({ text: trimmed, addedAt: Date.now() });
    }
    setQueueSize(queue.length);
    if (!cur) {
      showNextInQueue();
    } else {
      scheduleAdvance(); // backlog changed → recompute hold (catch-up)
    }
  }, [showNextInQueue, scheduleAdvance]);

  const clearTranslationQueue = useCallback(() => {
    translationQueueRef.current = [];
    if (displayTimeoutRef.current) {
      clearTimeout(displayTimeoutRef.current);
      displayTimeoutRef.current = null;
    }
    currentItemRef.current = null;
    setQueueSize(0);
  }, []);

  // Cleanup on unmount. React StrictMode double-invokes this cleanup, so we
  // MUST reset currentItemRef here or the queue would stall after remount.
  useEffect(() => {
    return () => {
      if (displayTimeoutRef.current) {
        clearTimeout(displayTimeoutRef.current);
        displayTimeoutRef.current = null;
      }
      currentItemRef.current = null;
    };
  }, []);

  // Ordered delivery of per-viewer translations: each incoming phrase gets a
  // sequence number; results (which may resolve out of order or time out) are
  // buffered and flushed strictly in sequence so nothing is skipped/reordered.
  const seqRef = useRef(0);
  const nextSeqToEmitRef = useRef(1);
  const pendingResultsRef = useRef(new Map());

  const resetOrdering = useCallback(() => {
    seqRef.current = 0;
    nextSeqToEmitRef.current = 1;
    pendingResultsRef.current = new Map();
  }, []);

  const emitInOrder = useCallback((seq, text) => {
    pendingResultsRef.current.set(seq, text);
    while (pendingResultsRef.current.has(nextSeqToEmitRef.current)) {
      const t = pendingResultsRef.current.get(nextSeqToEmitRef.current);
      pendingResultsRef.current.delete(nextSeqToEmitRef.current);
      nextSeqToEmitRef.current += 1;
      if (t) enqueueTranslation(t);
    }
  }, [enqueueTranslation]);

  // Set language from URL param if provided
  useEffect(() => {
    if (urlLang && !selectedLanguage) {
      const validLang = VISITOR_LANGUAGES.find(l => l.code === urlLang);
      if (validLang) {
        handleLanguageSelect(urlLang);
      }
    }
  }, [urlLang, selectedLanguage]);

  // Handle language selection - now shows mode selection screen after
  const handleLanguageSelect = (langCode) => {
    clearTranslationQueue();
    resetOrdering();
    setSelectedLanguage(langCode);
    localStorage.setItem(`room_${roomId}_language`, langCode);
    setShowLanguageSelect(false);
    
    // Check if user already selected an output mode
    const savedMode = localStorage.getItem(`room_${roomId}_outputMode`);
    if (!savedMode) {
      setShowModeSelect(true); // Show mode selection screen
      checkHeadphones(); // Start checking for headphones
    } else if (savedMode === "text-voice") {
      // If text+voice mode was saved, check if voice was also saved
      const savedVoice = localStorage.getItem(`room_${roomId}_voice`);
      if (!savedVoice) {
        loadVoicesForLanguage();
        setShowVoiceSelect(true);
      }
    }
  };
  
  // Handle output mode selection
  const handleModeSelect = (mode) => {
    setOutputMode(mode);
    localStorage.setItem(`room_${roomId}_outputMode`, mode);
    setShowModeSelect(false);
    
    // If text+voice mode, show voice selection
    if (mode === "text-voice") {
      loadVoicesForLanguage();
      setShowVoiceSelect(true);
    }
  };
  
  // Load available voices for the selected language
  const loadVoicesForLanguage = useCallback(() => {
    const voices = window.speechSynthesis.getVoices();
    const langCode = selectedLanguage || "en";
    const voiceLangCode = VOICE_LANG_MAP[langCode] || langCode;
    const langPrefix = voiceLangCode.split("-")[0];
    
    // Filter voices that match the selected language
    const matchingVoices = voices.filter(v => 
      v.lang.toLowerCase().startsWith(langPrefix.toLowerCase()) ||
      v.lang.toLowerCase().startsWith(langCode.toLowerCase())
    );
    
    // Check if we have native voices for this language
    const hasNative = matchingVoices.length > 0;
    setHasNativeVoices(hasNative);
    
    // If no native voices, show ALL available voices grouped by language
    // This way the user can at least hear something (even if in wrong language)
    let voicesToShow;
    if (hasNative) {
      voicesToShow = matchingVoices;
    } else {
      // Show all voices, but prioritize common languages
      const priorityLangs = ["en", "es", "pt", "fr", "de", "it", "zh", "ja", "ko", "ru"];
      const sortedVoices = [...voices].sort((a, b) => {
        const aLang = a.lang.split("-")[0].toLowerCase();
        const bLang = b.lang.split("-")[0].toLowerCase();
        const aIdx = priorityLangs.indexOf(aLang);
        const bIdx = priorityLangs.indexOf(bLang);
        if (aIdx === -1 && bIdx === -1) return 0;
        if (aIdx === -1) return 1;
        if (bIdx === -1) return -1;
        return aIdx - bIdx;
      });
      voicesToShow = sortedVoices.slice(0, 15); // Show up to 15 voices
    }
    
    setAvailableVoices(voicesToShow);
    
    // If user had a saved voice, check if it's still available
    const savedVoiceName = localStorage.getItem(`room_${roomId}_voice`);
    if (savedVoiceName) {
      const savedVoice = voicesToShow.find(v => v.name === savedVoiceName);
      if (savedVoice) {
        setSelectedVoice(savedVoiceName);
      } else if (voicesToShow.length > 0) {
        setSelectedVoice(voicesToShow[0].name);
      }
    } else if (voicesToShow.length > 0) {
      setSelectedVoice(voicesToShow[0].name);
    }
  }, [selectedLanguage, roomId]);
  
  // Handle voice selection
  const handleVoiceSelect = (voiceName) => {
    setSelectedVoice(voiceName);
    localStorage.setItem(`room_${roomId}_voice`, voiceName);
    setShowVoiceSelect(false);
  };
  
  // Check for headphones/external audio devices
  const checkHeadphones = async () => {
    setHasHeadphones(null); // Checking state
    try {
      // Request permission to enumerate devices
      await navigator.mediaDevices.getUserMedia({ audio: true }).catch(() => {});
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioOutputs = devices.filter(d => d.kind === "audiooutput");
      
      // Common headphone/earphone brand names and keywords
      const headphoneKeywords = [
        // Generic terms
        "headphone", "headset", "earphone", "earbuds", "earbud", "ear bud",
        "airpod", "bluetooth", "wireless", "bt ", " bt", "tws",
        "fone", "fones", "auricular", "écouteur", "kopfhörer", "cuffie",
        // Popular brands that make headphones/earbuds
        "oppo", "enco", "buds", "samsung", "galaxy", "jabra", "sony", "wh-", "wf-",
        "bose", "beats", "jbl", "sennheiser", "audio-technica", "akg",
        "anker", "soundcore", "skullcandy", "xiaomi", "redmi", "huawei", "freebuds",
        "oneplus", "nothing", "ear (", "pixel buds", "realme", "vivo",
        "edifier", "tozo", "soundpeats", "qcy", "baseus", "haylou",
        "marshall", "bang & olufsen", "b&o", "plantronics", "poly",
        // Connection types that suggest external audio
        "external", "usb", "aux", "wired"
      ];
      
      // Check if there's more than just the default speaker
      // Or if any device label suggests headphones/bluetooth
      const hasExternalAudio = audioOutputs.some(d => {
        const label = d.label.toLowerCase();
        
        // Check against all keywords
        const matchesKeyword = headphoneKeywords.some(keyword => label.includes(keyword));
        
        // Also consider: if there are multiple outputs and this isn't the built-in speaker
        const isExternalDevice = audioOutputs.length > 1 && 
          d.deviceId !== "default" && 
          !label.includes("speaker") && 
          !label.includes("alto-falante") &&
          !label.includes("altavoz") &&
          !label.includes("haut-parleur") &&
          !label.includes("lautsprecher") &&
          !label.includes("internal");
        
        return matchesKeyword || isExternalDevice;
      });
      
      setHasHeadphones(hasExternalAudio);
    } catch (e) {
      console.warn("[HEADPHONES] Could not check devices:", e);
      setHasHeadphones(false);
    }
  };
  
  // Text-to-Speech function
  const speakText = useCallback((text) => {
    if (outputMode !== "text-voice" || !text || text === lastSpokenTextRef.current) return;
    
    // Cancel any ongoing speech
    window.speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = VOICE_LANG_MAP[selectedLanguage] || "en-US";
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    
    // Use the selected voice if available
    const voices = window.speechSynthesis.getVoices();
    if (selectedVoice) {
      const voice = voices.find(v => v.name === selectedVoice);
      if (voice) {
        utterance.voice = voice;
      }
    } else {
      // Fallback: find any voice for this language
      const langVoice = voices.find(v => v.lang.startsWith(selectedLanguage) || v.lang.startsWith(VOICE_LANG_MAP[selectedLanguage]?.split("-")[0]));
      if (langVoice) {
        utterance.voice = langVoice;
      }
    }
    
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    
    window.speechSynthesis.speak(utterance);
    lastSpokenTextRef.current = text;
  }, [outputMode, selectedLanguage, selectedVoice]);
  
  // Speak translation when it changes
  useEffect(() => {
    if (translation && outputMode === "text-voice") {
      speakText(translation);
    }
  }, [translation, outputMode, speakText]);
  
  // Load voices (some browsers need this)
  useEffect(() => {
    const loadVoices = () => {
      window.speechSynthesis.getVoices();
    };
    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
  }, []);

  // Save settings to localStorage
  useEffect(() => {
    localStorage.setItem("publicRoom_fontSize", fontSize.toString());
    localStorage.setItem("publicRoom_bgColor", bgColor);
    localStorage.setItem("publicRoom_textColor", textColor);
    localStorage.setItem("publicRoom_showOriginal", showOriginal.toString());
    localStorage.setItem("publicRoom_readingSpeed", readingSpeed.toString());
  }, [fontSize, bgColor, textColor, showOriginal, readingSpeed]);

  // Translate text to visitor's selected language
  // `presenterText`/`presenterTargetLang`: translation already produced by the
  // presenter. If it matches the viewer's language we use it directly (no extra
  // GPT call, zero added latency). Otherwise we translate with a hard timeout.
  const translateForViewer = useCallback(async (originalText, sourceLang, presenterText, presenterTargetLang) => {
    if (!originalText || !selectedLanguage) return;
    const seq = ++seqRef.current;

    if (sourceLang === selectedLanguage) {
      emitInOrder(seq, originalText);
      return;
    }
    if (presenterText && presenterTargetLang === selectedLanguage) {
      emitInOrder(seq, presenterText);
      return;
    }

    setIsTranslating(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(`${BACKEND_URL}/api/rooms/translate-for-viewer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify({
          text: originalText,
          source_lang: sourceLang,
          target_lang: selectedLanguage,
          room_id: roomId
        })
      });
      const data = await response.json();
      emitInOrder(seq, (data.success && data.translated_text) ? data.translated_text : originalText);
    } catch (e) {
      console.error("[ROOM] Translation error:", e?.name === "AbortError" ? "timeout" : e);
      emitInOrder(seq, presenterText || originalText);
    } finally {
      clearTimeout(timeoutId);
      setIsTranslating(false);
    }
  }, [selectedLanguage, roomId, emitInOrder]);

  // Connect to room WebSocket
  const connectToRoom = useCallback(() => {
    if (!roomId || !selectedLanguage) return;
    
    const wsUrl = BACKEND_URL.replace("https://", "wss://").replace("http://", "ws://");
    const ws = new WebSocket(`${wsUrl}/api/ws/room/${roomId.toUpperCase()}/viewer?lang=${selectedLanguage}`);
    
    ws.onopen = () => {
      console.log("[ROOM] Connected to room", roomId, "with language", selectedLanguage);
      setConnected(true);
      setError(null);
    };
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log("[ROOM] Message:", data.type);
        
        if (data.type === "welcome") {
          setRoom(data.room);
          setViewerCount(data.room.viewer_count);
          // If there's already a translation, translate it for the viewer
          const lt = data.room.last_translation;
          if (lt?.original_text && lt.original_text !== lastOriginalRef.current) {
            lastOriginalRef.current = lt.original_text;
            setOriginal(lt.original_text);
            translateForViewer(lt.original_text, lt.source_lang || data.room.source_lang, lt.text, lt.target_lang || data.room.target_lang);
          }
        } else if (data.type === "translation") {
          // Received original text from presenter - translate for this viewer
          const originalText = data.data.original_text || data.data.original || data.data.text;
          const sourceLang = data.data.source_lang || room?.source_lang || "pt";
          
          if (originalText && originalText !== lastOriginalRef.current) {
            lastOriginalRef.current = originalText;
            setOriginal(originalText);
            translateForViewer(originalText, sourceLang, data.data.text, data.data.target_lang || room?.target_lang);
          }
        } else if (data.type === "viewer_count") {
          setViewerCount(data.count);
        } else if (data.type === "room_closed") {
          setError("publicRoomSessionEnded");
          setConnected(false);
        } else if (data.type === "session_started") {
          setRoom(prev => prev ? { ...prev, is_session_live: true } : prev);
          clearTranslationQueue();
          resetOrdering();
          setTranslation("");
        } else if (data.type === "session_ended") {
          setRoom(prev => prev ? { ...prev, is_session_live: false } : prev);
          clearTranslationQueue();
          resetOrdering();
          setTranslation("");
        } else if (data.type === "room_deleted") {
          setError("publicRoomDeleted");
          setConnected(false);
        } else if (data.type === "keepalive") {
          ws.send(JSON.stringify({ type: "ping" }));
        }
      } catch (e) {
        console.error("[ROOM] Error parsing message:", e);
      }
    };
    
    ws.onclose = (event) => {
      console.log("[ROOM] Disconnected:", event.code, event.reason);
      setConnected(false);
      wsRef.current = null;
      
      if (event.code !== 4004 && !error) {
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log("[ROOM] Attempting to reconnect...");
          connectToRoom();
        }, 3000);
      }
    };
    
    ws.onerror = (error) => {
      console.error("[ROOM] WebSocket error:", error);
    };
    
    wsRef.current = ws;
  }, [roomId, selectedLanguage, error, room, translateForViewer, clearTranslationQueue, resetOrdering]);

  // Initial connection - check if room exists
  useEffect(() => {
    const checkRoom = async () => {
      try {
        let response = await fetch(`${BACKEND_URL}/api/rooms/${roomId}`);
        let data = await response.json();
        
        if (!data.success) {
          response = await fetch(`${BACKEND_URL}/api/rooms/permanent/${roomId}`);
          data = await response.json();
        }
        
        if (!data.success) {
          setError("publicRoomErrorNotFound");
          return;
        }
        
        setRoom(data.room);
        
        // Only connect to WebSocket if language is already selected
        if (selectedLanguage) {
          connectToRoom();
        }
      } catch (e) {
        setError("publicRoomErrorServer");
      }
    };
    
    if (roomId) {
      checkRoom();
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [roomId]);

  // Connect when language is selected
  useEffect(() => {
    if (selectedLanguage && room && !wsRef.current) {
      connectToRoom();
    }
  }, [selectedLanguage, room, connectToRoom]);

  // Send periodic pings
  useEffect(() => {
    if (!connected || !wsRef.current) return;
    
    const interval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "ping" }));
      }
    }, 20000);
    
    return () => clearInterval(interval);
  }, [connected]);

  // Fullscreen handling
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Apply URL params for OBS mode
  const effectiveBgColor = obsMode ? (urlBgColor ? `#${urlBgColor}` : "transparent") : bgColor;
  const effectiveTextColor = urlTextColor ? `#${urlTextColor}` : textColor;
  const effectiveFontSize = urlFontSize ? parseInt(urlFontSize) : fontSize;

  // Get selected language info
  const selectedLangInfo = VISITOR_LANGUAGES.find(l => l.code === selectedLanguage);

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-800 rounded-xl p-8 max-w-md text-center">
          <WifiOff className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">{t("publicRoomUnavailable")}</h1>
          <p className="text-gray-400 mb-6">{t(error)}</p>
          <a 
            href="/"
            className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors"
          >
            {t("publicRoomBackHome")}
          </a>
        </div>
      </div>
    );
  }

  // Loading state
  if (!room) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">{t("publicRoomConnecting")} {roomId?.toUpperCase()}...</p>
        </div>
      </div>
    );
  }

  // Language selection screen
  if (showLanguageSelect && !obsMode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="max-w-4xl w-full">
          {/* Room info */}
          <div className="text-center mb-6">
            <p className="text-gray-500 text-sm mb-2">{t("publicRoomTitle")}</p>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">{room.name || roomId?.toUpperCase()}</h1>
            {room.presenter_name && (
              <p className="text-gray-400 text-sm">{t("publicRoomBy")} {room.presenter_name}</p>
            )}
          </div>
          
          {/* Language selection */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-4 md:p-6 border border-gray-700">
            <div className="text-center mb-4">
              <Globe className="w-10 h-10 text-blue-400 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-white mb-1">{t("publicRoomSelectLanguage")}</h2>
              <p className="text-gray-400 text-sm">{t("publicRoomLanguageHint")}</p>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {VISITOR_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageSelect(lang.code)}
                  className="flex items-center gap-2 p-2.5 bg-gray-700/50 hover:bg-blue-600/30 border border-gray-600 hover:border-blue-500 rounded-lg transition-all text-left group"
                >
                  <span className="text-lg">{lang.flag}</span>
                  <span className="text-white text-sm font-medium group-hover:text-blue-300 truncate">{lang.name}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Footer */}
          <p className="text-center text-gray-600 text-sm mt-6">
            {t("publicRoomPoweredBy")}
          </p>
        </div>
      </div>
    );
  }
  
  // Output mode selection screen (Text only or Text + Voice)
  if (showModeSelect && !obsMode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="max-w-xl w-full">
          {/* Room info */}
          <div className="text-center mb-6">
            <p className="text-gray-500 text-sm mb-2">{t("publicRoomTitle")}</p>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">{room.name || roomId?.toUpperCase()}</h1>
            {selectedLangInfo && (
              <p className="text-gray-400 text-sm flex items-center justify-center gap-2">
                <span>{selectedLangInfo.flag}</span>
                <span>{selectedLangInfo.name}</span>
              </p>
            )}
          </div>
          
          {/* Mode selection */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700">
            <div className="text-center mb-6">
              <Volume2 className="w-10 h-10 text-blue-400 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-white mb-1">{tRoom("publicRoomSelectMode")}</h2>
              <p className="text-gray-400 text-sm">{tRoom("publicRoomModeHint")}</p>
            </div>
            
            <div className="space-y-3">
              {/* Text Only Option */}
              <button
                onClick={() => handleModeSelect("text-only")}
                className="w-full flex items-center gap-4 p-4 bg-gray-700/50 hover:bg-blue-600/30 border border-gray-600 hover:border-blue-500 rounded-xl transition-all text-left group"
              >
                <div className="w-12 h-12 bg-gray-600 group-hover:bg-blue-600 rounded-full flex items-center justify-center transition-colors">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-medium group-hover:text-blue-300">{tRoom("publicRoomTextOnly")}</h3>
                  <p className="text-gray-400 text-sm">{tRoom("publicRoomTextOnlyDesc")}</p>
                </div>
              </button>
              
              {/* Text + Voice Option */}
              <button
                onClick={() => hasHeadphones ? handleModeSelect("text-voice") : null}
                disabled={hasHeadphones === false}
                className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all text-left group border ${
                  hasHeadphones === false 
                    ? "bg-gray-800/30 border-gray-700 cursor-not-allowed opacity-60" 
                    : "bg-gray-700/50 hover:bg-green-600/30 border-gray-600 hover:border-green-500"
                }`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                  hasHeadphones === false ? "bg-gray-700" : "bg-gray-600 group-hover:bg-green-600"
                }`}>
                  <Headphones className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className={`font-medium ${hasHeadphones === false ? "text-gray-500" : "text-white group-hover:text-green-300"}`}>
                    {tRoom("publicRoomTextVoice")}
                  </h3>
                  <p className={`text-sm ${hasHeadphones === false ? "text-gray-600" : "text-gray-400"}`}>
                    {tRoom("publicRoomTextVoiceDesc")}
                  </p>
                  
                  {/* Headphones status */}
                  <div className="mt-2">
                    {hasHeadphones === null ? (
                      <span className="text-yellow-400 text-xs flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        {tRoom("publicRoomCheckingAudio")}
                      </span>
                    ) : hasHeadphones ? (
                      <span className="text-green-400 text-xs flex items-center gap-1">
                        <Headphones className="w-3 h-3" />
                        {tRoom("publicRoomHeadphonesDetected")}
                      </span>
                    ) : (
                      <span className="text-red-400 text-xs flex items-center gap-1">
                        <VolumeX className="w-3 h-3" />
                        {tRoom("publicRoomHeadphonesNotDetected")}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            </div>
            
            {/* Info about headphones requirement */}
            {hasHeadphones === false && (
              <div className="text-center mt-4">
                <p className="text-gray-500 text-xs mb-2">
                  {tRoom("publicRoomHeadphonesRequired")}
                </p>
                <button
                  onClick={() => setHasHeadphones(true)}
                  className="text-purple-400 hover:text-purple-300 text-xs underline transition-colors"
                >
                  {tRoom("publicRoomHeadphonesOverride")}
                </button>
              </div>
            )}
          </div>
          
          {/* Back button */}
          <button
            onClick={() => {
              setShowModeSelect(false);
              setShowLanguageSelect(true);
              setSelectedLanguage(null);
              localStorage.removeItem(`room_${roomId}_language`);
            }}
            className="w-full mt-4 text-gray-500 hover:text-gray-300 text-sm transition-colors flex items-center justify-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            {tRoom("publicRoomChangeLanguage")}
          </button>
          
          {/* Footer */}
          <p className="text-center text-gray-600 text-sm mt-6">
            {t("publicRoomPoweredBy")}
          </p>
        </div>
      </div>
    );
  }
  
  // Voice selection screen (after choosing text+voice mode)
  if (showVoiceSelect && !obsMode) {
    // Function to test a voice
    const testVoice = (voiceName) => {
      window.speechSynthesis.cancel();
      const voices = window.speechSynthesis.getVoices();
      const voice = voices.find(v => v.name === voiceName);
      if (voice) {
        const testText = translations[selectedLanguage]?.publicRoomVoicePreview || "Hello, this is a voice test.";
        const utterance = new SpeechSynthesisUtterance(testText);
        utterance.voice = voice;
        utterance.lang = voice.lang;
        window.speechSynthesis.speak(utterance);
      }
    };
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="max-w-xl w-full">
          {/* Room info */}
          <div className="text-center mb-6">
            <p className="text-gray-500 text-sm mb-2">{t("publicRoomTitle")}</p>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">{room.name || roomId?.toUpperCase()}</h1>
            {selectedLangInfo && (
              <p className="text-gray-400 text-sm flex items-center justify-center gap-2">
                <span>{selectedLangInfo.flag}</span>
                <span>{selectedLangInfo.name}</span>
              </p>
            )}
          </div>
          
          {/* Voice selection */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700">
            <div className="text-center mb-6">
              <Volume2 className="w-10 h-10 text-purple-400 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-white mb-1">{tRoom("publicRoomSelectVoice")}</h2>
              <p className="text-gray-400 text-sm">{tRoom("publicRoomSelectVoiceHint")}</p>
            </div>
            
            {availableVoices.length === 0 ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-3" />
                <p className="text-gray-400">{tRoom("publicRoomCheckingAudio")}</p>
              </div>
            ) : (
              <>
                {/* Warning if no native voices */}
                {!hasNativeVoices && (
                  <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <p className="text-yellow-400 text-sm">
                      {tRoom("publicRoomNoNativeVoices").replace("{language}", selectedLangInfo?.name || selectedLanguage)}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-yellow-400/70 text-xs">
                        {tRoom("publicRoomInstallVoices")}
                      </p>
                      <button
                        onClick={() => setShowVoiceHelpModal(true)}
                        className="flex items-center gap-1 px-2 py-1 bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/50 rounded text-yellow-400 text-xs transition-colors"
                      >
                        <HelpCircle className="w-3 h-3" />
                        {tRoom("publicRoomHowToInstallVoices")}
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Help button (always visible) */}
                {hasNativeVoices && (
                  <div className="mb-4 flex justify-end">
                    <button
                      onClick={() => setShowVoiceHelpModal(true)}
                      className="flex items-center gap-1 px-2 py-1 text-gray-400 hover:text-gray-300 text-xs transition-colors"
                    >
                      <HelpCircle className="w-3 h-3" />
                      {tRoom("publicRoomHowToInstallVoices")}
                    </button>
                  </div>
                )}
                
                <div className="space-y-2 max-h-64 overflow-y-auto">
                {availableVoices.map((voice, index) => (
                  <div
                    key={voice.name}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                      selectedVoice === voice.name 
                        ? "bg-purple-600/30 border border-purple-500" 
                        : "bg-gray-700/50 border border-gray-600 hover:bg-gray-700"
                    }`}
                    onClick={() => setSelectedVoice(voice.name)}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      selectedVoice === voice.name ? "bg-purple-500" : "bg-gray-600"
                    }`}>
                      <span className="text-white text-sm font-bold">{index + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium truncate ${
                        selectedVoice === voice.name ? "text-purple-300" : "text-white"
                      }`}>
                        {voice.name.replace(/Microsoft |Google |Apple /gi, "")}
                      </p>
                      <p className="text-gray-400 text-xs">{voice.lang}</p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        testVoice(voice.name);
                      }}
                      className="px-3 py-1.5 bg-gray-600 hover:bg-gray-500 rounded-lg text-xs text-white transition-colors"
                    >
                      {tRoom("publicRoomTestVoice")}
                    </button>
                  </div>
                ))}
              </div>
              </>
            )}
            
            {/* Confirm button */}
            {availableVoices.length > 0 && (
              <button
                onClick={() => handleVoiceSelect(selectedVoice)}
                disabled={!selectedVoice}
                className="w-full mt-4 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-colors"
              >
                {tRoom("publicRoomConfirm")}
              </button>
            )}
          </div>
          
          {/* Back button */}
          <button
            onClick={() => {
              setShowVoiceSelect(false);
              setShowModeSelect(true);
              setOutputMode(null);
              localStorage.removeItem(`room_${roomId}_outputMode`);
            }}
            className="w-full mt-4 text-gray-500 hover:text-gray-300 text-sm transition-colors flex items-center justify-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            {tRoom("publicRoomChangeLanguage")}
          </button>
          
          {/* Footer */}
          <p className="text-center text-gray-600 text-sm mt-6">
            {t("publicRoomPoweredBy")}
          </p>
        </div>
        
        {/* Voice Help Modal */}
        {showVoiceHelpModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
            <div className="bg-gray-800 rounded-2xl border border-gray-700 max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4 border-b border-gray-700 flex items-center justify-between sticky top-0 bg-gray-800 rounded-t-2xl">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-purple-400" />
                  {tRoom("publicRoomVoiceHelpTitle")}
                </h3>
                <button
                  onClick={() => setShowVoiceHelpModal(false)}
                  className="p-1 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              
              <div className="p-4 space-y-4">
                {/* Windows */}
                <div className="bg-gray-700/50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Monitor className="w-5 h-5 text-blue-400" />
                    <h4 className="text-white font-medium">{tRoom("publicRoomVoiceHelpWindows")}</h4>
                  </div>
                  <p className="text-gray-300 text-sm whitespace-pre-line">
                    {tRoom("publicRoomVoiceHelpWindowsSteps")}
                  </p>
                </div>
                
                {/* macOS */}
                <div className="bg-gray-700/50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Monitor className="w-5 h-5 text-gray-400" />
                    <h4 className="text-white font-medium">{tRoom("publicRoomVoiceHelpMac")}</h4>
                  </div>
                  <p className="text-gray-300 text-sm whitespace-pre-line">
                    {tRoom("publicRoomVoiceHelpMacSteps")}
                  </p>
                </div>
                
                {/* Android */}
                <div className="bg-gray-700/50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Smartphone className="w-5 h-5 text-green-400" />
                    <h4 className="text-white font-medium">{tRoom("publicRoomVoiceHelpAndroid")}</h4>
                  </div>
                  <p className="text-gray-300 text-sm whitespace-pre-line">
                    {tRoom("publicRoomVoiceHelpAndroidSteps")}
                  </p>
                </div>
                
                {/* iOS */}
                <div className="bg-gray-700/50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Smartphone className="w-5 h-5 text-gray-300" />
                    <h4 className="text-white font-medium">{tRoom("publicRoomVoiceHelpIOS")}</h4>
                  </div>
                  <p className="text-gray-300 text-sm whitespace-pre-line">
                    {tRoom("publicRoomVoiceHelpIOSSteps")}
                  </p>
                </div>
                
                {/* Tip */}
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
                  <p className="text-purple-300 text-sm">
                    💡 {tRoom("publicRoomVoiceHelpTip")}
                  </p>
                </div>
              </div>
              
              <div className="p-4 border-t border-gray-700">
                <button
                  onClick={() => setShowVoiceHelpModal(false)}
                  className="w-full py-3 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-xl transition-colors"
                >
                  {tRoom("publicRoomVoiceHelpClose")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: effectiveBgColor }}
    >
      {/* Header - Hidden in OBS mode */}
      {!obsMode && (
        <header className="bg-gray-800/80 backdrop-blur-sm border-b border-gray-700 px-4 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-white font-semibold">
                {tRoom("publicRoomHeaderRoom")}: <span className="text-blue-400">{roomId?.toUpperCase()}</span>
              </h1>
              {room.name && (
                <span className="text-gray-400 text-sm hidden md:inline">
                  {room.name}
                </span>
              )}
            </div>
            
            <div className="flex items-center gap-3">
              {/* Selected language indicator */}
              {selectedLangInfo && (
                <button
                  onClick={() => setShowLanguageSelect(true)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-gray-700/50 hover:bg-gray-600 rounded-lg text-sm transition-colors"
                  title={tRoom("publicRoomChangeLanguage")}
                >
                  <span>{selectedLangInfo.flag}</span>
                  <span className="text-white hidden sm:inline">{selectedLangInfo.name}</span>
                </button>
              )}
              
              {/* Change voice button - only in voice mode */}
              {outputMode === "text-voice" && (
                <button
                  onClick={() => setShowVoiceSelect(true)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-purple-600/30 hover:bg-purple-600/50 text-purple-300 hover:text-purple-200 rounded-lg text-sm transition-colors border border-purple-500/30"
                  title={tRoom("publicRoomChangeVoice")}
                  data-testid="change-voice-btn"
                >
                  <Volume2 className="w-4 h-4" />
                  <span className="hidden sm:inline">{tRoom("publicRoomChangeVoice")}</span>
                </button>
              )}
              
              {/* Voice mode indicator */}
              {outputMode === "text-voice" && (
                <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                  isSpeaking ? "bg-green-500/20 text-green-400" : "bg-purple-500/20 text-purple-400"
                }`}>
                  {isSpeaking ? (
                    <>
                      <Volume2 className="w-4 h-4 animate-pulse" />
                      <span className="hidden sm:inline">{tRoom("publicRoomSpeaking")}</span>
                    </>
                  ) : (
                    <>
                      <Headphones className="w-4 h-4" />
                      <span className="hidden sm:inline">{tRoom("publicRoomVoiceEnabled")}</span>
                    </>
                  )}
                </div>
              )}
              
              {/* Connection status */}
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                connected ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"
              }`}>
                {connected ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
                <span className="hidden sm:inline">{connected ? tRoom("publicRoomConnected") : tRoom("publicRoomReconnecting")}</span>
              </div>
              
              {/* Viewer count */}
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <Users className="w-4 h-4" />
                <span>{viewerCount}</span>
              </div>
              
              {/* Settings button */}
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
              >
                <Settings className="w-5 h-5" />
              </button>
              
              {/* Fullscreen button */}
              <button
                onClick={toggleFullscreen}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
              >
                {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </header>
      )}
      
      {/* Settings Panel */}
      {showSettings && !obsMode && (
        <div className="bg-gray-800 border-b border-gray-700 px-4 py-4">
          <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-gray-400 text-sm mb-1">{tRoom("publicRoomFontSize")}</label>
              <input
                type="range"
                min="24"
                max="96"
                value={fontSize}
                onChange={(e) => setFontSize(parseInt(e.target.value))}
                className="w-full"
              />
              <span className="text-gray-500 text-xs">{fontSize}px</span>
            </div>
            
            <div>
              <label className="block text-gray-400 text-sm mb-1">{tRoom("publicRoomTextColor")}</label>
              <input
                type="color"
                value={textColor}
                onChange={(e) => setTextColor(e.target.value)}
                className="w-full h-8 rounded cursor-pointer"
              />
            </div>
            
            <div>
              <label className="block text-gray-400 text-sm mb-1">{tRoom("publicRoomBgColor")}</label>
              <input
                type="color"
                value={bgColor}
                onChange={(e) => setBgColor(e.target.value)}
                className="w-full h-8 rounded cursor-pointer"
              />
            </div>
            
            <div className="flex items-center">
              <label className="flex items-center gap-2 text-gray-400 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={showOriginal}
                  onChange={(e) => setShowOriginal(e.target.checked)}
                  className="rounded"
                  data-testid="setting-show-original-checkbox"
                />
                {tRoom("publicRoomShowOriginal")}
              </label>
            </div>

            <div className="col-span-2 md:col-span-4">
              <label className="block text-gray-400 text-sm mb-1">
                {tRoom("publicRoomReadingSpeed")}
              </label>
              <div className="flex items-center gap-3">
                <span className="text-gray-500 text-xs w-16 text-right">
                  {tRoom("publicRoomReadingSpeedSlow")}
                </span>
                <input
                  type="range"
                  min="10"
                  max="22"
                  step="1"
                  value={readingSpeed}
                  onChange={(e) => setReadingSpeed(parseInt(e.target.value))}
                  className="flex-1"
                  data-testid="setting-reading-speed-slider"
                />
                <span className="text-gray-500 text-xs w-16">
                  {tRoom("publicRoomReadingSpeedFast")}
                </span>
              </div>
              <div className="text-center text-gray-500 text-xs mt-1">
                {readingSpeed <= 11
                  ? tRoom("publicRoomReadingSpeedSlow")
                  : readingSpeed >= 19
                  ? tRoom("publicRoomReadingSpeedFast")
                  : tRoom("publicRoomReadingSpeedNormal")}{" "}
                · {readingSpeed} {tRoom("publicRoomReadingSpeedUnit")}
                {queueSize > 0 && (
                  <span className="ml-2 text-yellow-500">
                    · {queueSize} {tRoom("publicRoomInQueue")}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Main Translation Display */}
      <main className="flex-1 flex items-center justify-center p-4 md:p-8">
        <div className="text-center max-w-5xl w-full">
          {/* Show waiting message for permanent rooms not in session */}
          {room?.is_permanent && room?.is_session_live === false && !translation ? (
            <div className="space-y-4">
              <p 
                className="font-medium opacity-70"
                style={{ 
                  color: effectiveTextColor, 
                  fontSize: `${effectiveFontSize * 0.6}px`,
                }}
              >
                {tRoom("publicRoomWaitingSession")}
              </p>
              <p 
                className="opacity-40"
                style={{ 
                  color: effectiveTextColor, 
                  fontSize: `${effectiveFontSize * 0.35}px`,
                }}
              >
                {tRoom("publicRoomSessionWillStart")}
              </p>
            </div>
          ) : (
            <>
              {/* Original text (smaller, above) */}
              {showOriginal && original && !obsMode && (
                <p 
                  className="mb-4 opacity-60"
                  style={{ 
                    color: effectiveTextColor, 
                    fontSize: `${effectiveFontSize * 0.5}px`,
                    lineHeight: 1.4
                  }}
                >
                  {original}
                </p>
              )}
              
              {/* Translated text (main) */}
              <p 
                className="font-medium leading-relaxed transition-all duration-300"
                style={{ 
                  color: effectiveTextColor, 
                  fontSize: `${effectiveFontSize}px`,
                  lineHeight: 1.3,
                  textShadow: obsMode ? "2px 2px 4px rgba(0,0,0,0.8)" : "none"
                }}
              >
                {translation || (
                  <span className="opacity-50">{tRoom("publicRoomWaitingTranslation")}</span>
                )}
              </p>
            </>
          )}
        </div>
      </main>
      
      {/* Footer - Hidden in OBS mode */}
      {!obsMode && (
        <footer className="bg-gray-800/80 backdrop-blur-sm border-t border-gray-700 px-4 py-2">
          <div className="max-w-7xl mx-auto flex items-center justify-between text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <Volume2 className="w-4 h-4" />
              {room.source_lang?.toUpperCase()} → {selectedLanguage?.toUpperCase() || room.target_lang?.toUpperCase()}
            </div>
            <a 
              href="/" 
              className="hover:text-gray-300 transition-colors"
            >
              LCF Translator
            </a>
          </div>
        </footer>
      )}
    </div>
  );
}
